import {
	differenceInDays,
	format,
	formatISO,
	isAfter,
	isBefore,
	isToday,
	isTomorrow,
	parseISO,
	startOfToday
} from "date-fns";

export interface DueDateDisplay {
	text: string;
	colorClass: string;
}

export const formatDate = (dateInput: Date | string, addYears?: number): string => {
	const date = new Date(dateInput);
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const year = date.getFullYear() + (addYears ?? 0);

	return `${day}/${month}/${year}`;
};

export const formatDateForInput = (dateString?: string | null): string => {
	if (!dateString) return "";
	const date = parseISO(dateString);
	const isoString = formatISO(date, { representation: 'date' });
	return isoString || "";
};

export const formatDateTime = (date: string | Date): string => {
	return new Date(date).toLocaleDateString("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const formatRelativeDateTime = (dateString: string): string => {
	const date = new Date(dateString);
	const now = new Date();
	const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

	if (diffInHours < 24) {
		return date.toLocaleTimeString("fr-FR", {
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	if (diffInHours < 24 * 7) {
		return date.toLocaleDateString("fr-FR", {
			weekday: "short",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	return date.toLocaleDateString("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const formatSimpleDate = (date: string | Date): string => {
	return new Date(date).toLocaleDateString("fr-FR");
};

export const formatDueDate = (dateString: string): string => {
	const dueDate = parseISO(dateString);
	const now = startOfToday();

	if (isToday(dueDate)) {
		return "Aujourd'hui";
	}
	if (isTomorrow(dueDate)) {
		return "Demain";
	}
	if (isBefore(dueDate, now)) {
		const daysPast = differenceInDays(now, dueDate);
		return `En retard de ${daysPast} jour${daysPast > 1 ? "s" : ""}`;
	}
	if (isAfter(dueDate, now)) {
		const daysLeft = differenceInDays(dueDate, now);
		return `Dans ${daysLeft} jour${daysLeft > 1 ? "s" : ""}`;
	}
	return format(dueDate, "dd/MM/yyyy");
};

export const getDueDateColor = (dateString: string): string => {
	const dueDate = parseISO(dateString);
	const now = startOfToday();

	if (isBefore(dueDate, now)) {
		return "text-red-600 font-medium";
	}
	if (isToday(dueDate) || isTomorrow(dueDate)) {
		return "text-orange-600 font-medium";
	}
	return "text-gray-500";
};
